/**
 * Permission constants for Agentic Loan Audit.
 *
 * Every permission is a dot-separated string in the format `module.action`.
 * Modules: auth, access, ingestion, parsing, validation, scoring, detection,
 *          reporting, evidence, admin, masters, settings
 * Actions: view, create, edit, delete, export, approve
 *
 * Not every module implements every action — the catalog below reflects what
 * each module actually supports.
 *
 * ---
 * HOW TO ADD A NEW PERMISSION:
 * 1. Add the constant here (group by module)
 * 2. Add it to the PERMISSION_CATALOG array at the bottom so the seeder
 *    inserts it into the database on next deploy
 * 3. Add it to the relevant backend route guard
 * 4. Add it to the relevant frontend <ProtectedRoute> / menu visibility check
 * 5. Grant it to the appropriate roles via the Access Manager UI after deploy
 *
 * Never rename or delete a permission once deployed — roles depend on them.
 * Instead, deprecate in code comments and leave the permission in the DB.
 */
// -------------------------------------------------------------------------
// Access Manager (users, roles, permissions, page registry)
// -------------------------------------------------------------------------
export const ACCESS_VIEW = 'access.view';
export const ACCESS_MANAGE_USERS = 'access.manage_users';
export const ACCESS_MANAGE_ROLES = 'access.manage_roles';
export const ACCESS_MANAGE_PERMISSIONS = 'access.manage_permissions';
export const ACCESS_VIEW_PAGE_REGISTRY = 'access.view_page_registry';
// -------------------------------------------------------------------------
// Ingestion
// -------------------------------------------------------------------------
export const INGESTION_VIEW = 'ingestion.view';
export const INGESTION_UPLOAD = 'ingestion.upload';
export const INGESTION_API_USE = 'ingestion.api_use';
export const INGESTION_DELETE = 'ingestion.delete';
export const INGESTION_RETRIGGER = 'ingestion.retrigger';
// -------------------------------------------------------------------------
// Parsing
// -------------------------------------------------------------------------
export const PARSING_VIEW = 'parsing.view';
export const PARSING_REPARSE = 'parsing.reparse';
export const PARSING_OVERRIDE_CLASSIFICATION = 'parsing.override_classification';
// -------------------------------------------------------------------------
// Validation (UC1, UC2, UC3)
// -------------------------------------------------------------------------
export const VALIDATION_VIEW = 'validation.view';
export const VALIDATION_RERUN = 'validation.rerun';
export const VALIDATION_RESOLVE = 'validation.resolve';
export const VALIDATION_OVERRIDE_FINDING = 'validation.override_finding';
export const VALIDATION_EXPORT = 'validation.export';
// -------------------------------------------------------------------------
// Scoring
// -------------------------------------------------------------------------
export const SCORING_VIEW = 'scoring.view';
export const SCORING_RECOMPUTE = 'scoring.recompute';
// -------------------------------------------------------------------------
// Detection (cross-file patterns, branch/officer anomalies)
// -------------------------------------------------------------------------
export const DETECTION_VIEW = 'detection.view';
export const DETECTION_VIEW_OFFICER_LEVEL = 'detection.view_officer_level';
export const DETECTION_RERUN = 'detection.rerun';
export const DETECTION_EXPORT = 'detection.export';
// -------------------------------------------------------------------------
// Reporting
// -------------------------------------------------------------------------
export const REPORTING_VIEW = 'reporting.view';
export const REPORTING_CREATE_OBSERVATION = 'reporting.create_observation';
export const REPORTING_APPROVE_OBSERVATION = 'reporting.approve_observation';
export const REPORTING_EXPORT = 'reporting.export';
// -------------------------------------------------------------------------
// Evidence
// -------------------------------------------------------------------------
export const EVIDENCE_VIEW = 'evidence.view';
export const EVIDENCE_DOWNLOAD = 'evidence.download';
// -------------------------------------------------------------------------
// Admin (config, thresholds, audit log)
// -------------------------------------------------------------------------
export const ADMIN_VIEW = 'admin.view';
export const ADMIN_EDIT_CONFIG = 'admin.edit_config';
export const ADMIN_VIEW_AUDIT_LOG = 'admin.view_audit_log';
// -------------------------------------------------------------------------
// Masters (policies, checklists, DoA, prompts, products, branches, ...)
// -------------------------------------------------------------------------
export const MASTERS_VIEW = 'masters.view';
export const MASTERS_EDIT = 'masters.edit';
export const MASTERS_CREATE_TYPE = 'masters.create_type';
export const MASTERS_ROLLBACK = 'masters.rollback';
export const MASTERS_EXPORT = 'masters.export';
export const MASTERS_IMPORT = 'masters.import';
// -------------------------------------------------------------------------
// Settings (documentation viewer, system info)
// -------------------------------------------------------------------------
export const SETTINGS_VIEW = 'settings.view';
// -------------------------------------------------------------------------
// Dashboard (the default landing page)
// -------------------------------------------------------------------------
export const DASHBOARD_VIEW = 'dashboard.view';
// -------------------------------------------------------------------------
// Notifications
// -------------------------------------------------------------------------
export const NOTIFICATIONS_VIEW = 'notifications.view';
export const NOTIFICATIONS_MANAGE_PREFS = 'notifications.manage_prefs';
export const NOTIFICATIONS_SEND_BROADCAST = 'notifications.send_broadcast';
// -------------------------------------------------------------------------
// LOS (Loan Origination System data) — Batch 1, 17-point checklist
// -------------------------------------------------------------------------
export const LOS_UPLOAD = 'los.upload';
export const LOS_VIEW = 'los.view';
export const LOS_MANAGE = 'los.manage';
export const PERMISSION_CATALOG = [
    // Access
    { key: ACCESS_VIEW, module: 'access', action: 'view', description: 'View users, roles, and permissions in Access Manager' },
    { key: ACCESS_MANAGE_USERS, module: 'access', action: 'manage_users', description: 'Create, edit, and disable users; assign roles' },
    { key: ACCESS_MANAGE_ROLES, module: 'access', action: 'manage_roles', description: 'Create and edit roles and their permission sets' },
    { key: ACCESS_MANAGE_PERMISSIONS, module: 'access', action: 'manage_permissions', description: 'Grant direct permissions to users (bypassing roles)' },
    { key: ACCESS_VIEW_PAGE_REGISTRY, module: 'access', action: 'view_page_registry', description: 'View the auto-populated registry of frontend pages' },
    // Dashboard
    { key: DASHBOARD_VIEW, module: 'dashboard', action: 'view', description: 'View the main dashboard' },
    // Ingestion
    { key: INGESTION_VIEW, module: 'ingestion', action: 'view', description: 'View ingested loan files and their processing status' },
    { key: INGESTION_UPLOAD, module: 'ingestion', action: 'upload', description: 'Upload loan files via the web UI' },
    { key: INGESTION_API_USE, module: 'ingestion', action: 'api_use', description: 'Push loan files via the integration API' },
    { key: INGESTION_DELETE, module: 'ingestion', action: 'delete', description: 'Delete an ingested file and all derived data' },
    { key: INGESTION_RETRIGGER, module: 'ingestion', action: 'retrigger', description: 'Retrigger processing for a file (reparse, revalidate, rescore)' },
    // Parsing
    { key: PARSING_VIEW, module: 'parsing', action: 'view', description: 'View OCR output, classifications, and extracted fields' },
    { key: PARSING_REPARSE, module: 'parsing', action: 'reparse', description: 'Manually retrigger parsing for a document' },
    { key: PARSING_OVERRIDE_CLASSIFICATION, module: 'parsing', action: 'override_classification', description: 'Manually change a document\u2019s classification' },
    // Validation
    { key: VALIDATION_VIEW, module: 'validation', action: 'view', description: 'View findings from UC1, UC2, and UC3' },
    { key: VALIDATION_RERUN, module: 'validation', action: 'rerun', description: 'Rerun validation for a loan file' },
    { key: VALIDATION_RESOLVE, module: 'validation', action: 'resolve', description: 'Resolve findings as accepted, waived, or false-positive' },
    { key: VALIDATION_OVERRIDE_FINDING, module: 'validation', action: 'override_finding', description: 'Admin override: bypass a rule for a specific loan file' },
    { key: VALIDATION_EXPORT, module: 'validation', action: 'export', description: 'Export validation results to Excel or PDF' },
    // Scoring
    { key: SCORING_VIEW, module: 'scoring', action: 'view', description: 'View compliance scores and severity tags' },
    { key: SCORING_RECOMPUTE, module: 'scoring', action: 'recompute', description: 'Recompute scores for a file or cohort' },
    // Detection
    { key: DETECTION_VIEW, module: 'detection', action: 'view', description: 'View cross-file patterns and anomalies at branch and product level' },
    { key: DETECTION_VIEW_OFFICER_LEVEL, module: 'detection', action: 'view_officer_level', description: 'View officer-level anomalies (restricted — governance-sensitive)' },
    { key: DETECTION_RERUN, module: 'detection', action: 'rerun', description: 'Rerun detection analysis on a cohort' },
    { key: DETECTION_EXPORT, module: 'detection', action: 'export', description: 'Export detection findings' },
    // Reporting
    { key: REPORTING_VIEW, module: 'reporting', action: 'view', description: 'View dashboards and scorecards' },
    { key: REPORTING_CREATE_OBSERVATION, module: 'reporting', action: 'create_observation', description: 'Create or edit auto-generated audit observations' },
    { key: REPORTING_APPROVE_OBSERVATION, module: 'reporting', action: 'approve_observation', description: 'Approve observations for inclusion in final audit reports' },
    { key: REPORTING_EXPORT, module: 'reporting', action: 'export', description: 'Export reports to PDF or Excel' },
    // Evidence
    { key: EVIDENCE_VIEW, module: 'evidence', action: 'view', description: 'View evidence linked to findings' },
    { key: EVIDENCE_DOWNLOAD, module: 'evidence', action: 'download', description: 'Download original source documents used as evidence' },
    // Admin
    { key: ADMIN_VIEW, module: 'admin', action: 'view', description: 'View admin screens: config, thresholds, audit log' },
    { key: ADMIN_EDIT_CONFIG, module: 'admin', action: 'edit_config', description: 'Edit system configuration values and thresholds' },
    { key: ADMIN_VIEW_AUDIT_LOG, module: 'admin', action: 'view_audit_log', description: 'View the immutable system audit log' },
    // Masters
    { key: MASTERS_VIEW, module: 'masters', action: 'view', description: 'View master data (policies, checklists, DoA, prompts, ...)' },
    { key: MASTERS_EDIT, module: 'masters', action: 'edit', description: 'Create and edit master records' },
    { key: MASTERS_CREATE_TYPE, module: 'masters', action: 'create_type', description: 'Define new master data types' },
    { key: MASTERS_ROLLBACK, module: 'masters', action: 'rollback', description: 'Roll back master data to a previous version' },
    { key: MASTERS_EXPORT, module: 'masters', action: 'export', description: 'Export a master type\u2019s records as JSON' },
    { key: MASTERS_IMPORT, module: 'masters', action: 'import', description: 'Import master records from JSON' },
    // Notifications
    { key: NOTIFICATIONS_VIEW, module: 'notifications', action: 'view', description: 'View your own notifications' },
    { key: NOTIFICATIONS_MANAGE_PREFS, module: 'notifications', action: 'manage_prefs', description: 'Manage your notification preferences (email, digest)' },
    { key: NOTIFICATIONS_SEND_BROADCAST, module: 'notifications', action: 'send_broadcast', description: 'Send a platform-wide broadcast notification' },
    // LOS (Loan Origination System data) — Batch 1
    { key: LOS_UPLOAD, module: 'los', action: 'upload', description: 'Upload LOS data (JSON or trigger REST pull) for a loan file' },
    { key: LOS_VIEW, module: 'los', action: 'view', description: 'View LOS data attached to a loan file' },
    { key: LOS_MANAGE, module: 'los', action: 'manage', description: 'Replace or delete LOS data on a loan file (admin only)' },
    // Settings
    { key: SETTINGS_VIEW, module: 'settings', action: 'view', description: 'View system settings and module documentation' },
];
export const BUILTIN_ROLES = [
    {
        key: 'auditor',
        name: 'Auditor',
        description: 'Can view all findings, evidence, and reports; create observations; cannot edit masters or manage access',
        permissions: [
            DASHBOARD_VIEW,
            INGESTION_VIEW,
            PARSING_VIEW,
            VALIDATION_VIEW, VALIDATION_EXPORT,
            SCORING_VIEW,
            DETECTION_VIEW,
            REPORTING_VIEW, REPORTING_CREATE_OBSERVATION, REPORTING_EXPORT,
            EVIDENCE_VIEW, EVIDENCE_DOWNLOAD,
            MASTERS_VIEW,
            SETTINGS_VIEW,
            NOTIFICATIONS_VIEW, NOTIFICATIONS_MANAGE_PREFS,
            LOS_VIEW,
        ],
    },
    {
        key: 'senior_auditor',
        name: 'Senior Auditor',
        description: 'Auditor + can override findings, approve observations, view officer-level detection',
        permissions: [
            DASHBOARD_VIEW,
            INGESTION_VIEW, INGESTION_UPLOAD,
            PARSING_VIEW, PARSING_REPARSE,
            VALIDATION_VIEW, VALIDATION_RERUN, VALIDATION_RESOLVE, VALIDATION_OVERRIDE_FINDING, VALIDATION_EXPORT,
            SCORING_VIEW, SCORING_RECOMPUTE,
            DETECTION_VIEW, DETECTION_VIEW_OFFICER_LEVEL, DETECTION_EXPORT,
            REPORTING_VIEW, REPORTING_CREATE_OBSERVATION, REPORTING_APPROVE_OBSERVATION, REPORTING_EXPORT,
            EVIDENCE_VIEW, EVIDENCE_DOWNLOAD,
            MASTERS_VIEW,
            SETTINGS_VIEW,
            NOTIFICATIONS_VIEW, NOTIFICATIONS_MANAGE_PREFS,
            LOS_VIEW, LOS_UPLOAD,
        ],
    },
    {
        key: 'audit_manager',
        name: 'Audit Manager',
        description: 'Senior Auditor + can edit masters, manage users within the audit team, view audit log',
        permissions: [
            DASHBOARD_VIEW,
            ACCESS_VIEW, ACCESS_MANAGE_USERS,
            INGESTION_VIEW, INGESTION_UPLOAD, INGESTION_API_USE, INGESTION_RETRIGGER,
            PARSING_VIEW, PARSING_REPARSE, PARSING_OVERRIDE_CLASSIFICATION,
            VALIDATION_VIEW, VALIDATION_RERUN, VALIDATION_RESOLVE, VALIDATION_OVERRIDE_FINDING, VALIDATION_EXPORT,
            SCORING_VIEW, SCORING_RECOMPUTE,
            DETECTION_VIEW, DETECTION_VIEW_OFFICER_LEVEL, DETECTION_RERUN, DETECTION_EXPORT,
            REPORTING_VIEW, REPORTING_CREATE_OBSERVATION, REPORTING_APPROVE_OBSERVATION, REPORTING_EXPORT,
            EVIDENCE_VIEW, EVIDENCE_DOWNLOAD,
            ADMIN_VIEW, ADMIN_VIEW_AUDIT_LOG, ADMIN_EDIT_CONFIG,
            MASTERS_VIEW, MASTERS_EDIT, MASTERS_ROLLBACK, MASTERS_EXPORT, MASTERS_IMPORT,
            SETTINGS_VIEW,
            NOTIFICATIONS_VIEW, NOTIFICATIONS_MANAGE_PREFS, NOTIFICATIONS_SEND_BROADCAST,
            LOS_VIEW, LOS_UPLOAD, LOS_MANAGE,
        ],
    },
    {
        key: 'operator',
        name: 'Operator',
        description: 'Can upload files and view their processing status only. No access to findings or reports',
        permissions: [
            DASHBOARD_VIEW,
            INGESTION_VIEW, INGESTION_UPLOAD,
            SETTINGS_VIEW,
            NOTIFICATIONS_VIEW, NOTIFICATIONS_MANAGE_PREFS,
            LOS_UPLOAD,
        ],
    },
    {
        key: 'read_only',
        name: 'Read-only',
        description: 'View everything, change nothing. Useful for executive sponsors and external reviewers',
        permissions: [
            DASHBOARD_VIEW,
            INGESTION_VIEW,
            PARSING_VIEW,
            VALIDATION_VIEW,
            SCORING_VIEW,
            DETECTION_VIEW,
            REPORTING_VIEW,
            EVIDENCE_VIEW,
            MASTERS_VIEW,
            SETTINGS_VIEW,
            NOTIFICATIONS_VIEW, NOTIFICATIONS_MANAGE_PREFS,
            LOS_VIEW,
        ],
    },
];
//# sourceMappingURL=permissions.js.map